import { useEffect, useRef } from "react";

// Stack of currently open modals (drawer, dialog). Only the top-most one
// reacts to Escape / Tab, so a confirmation dialog opened over the details
// drawer closes alone instead of closing both.
const modalStack = [];

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared accessibility behaviour for modal surfaces:
 *  - locks page scroll while open
 *  - moves focus in (initialFocusRef, else the container) and restores it
 *  - Escape closes, Tab/Shift+Tab stay inside the container
 */
export default function useModalA11y({
  open,
  onClose,
  containerRef,
  initialFocusRef,
}) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;

    const token = {};
    modalStack.push(token);

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const container = containerRef.current;
    const initialTarget =
      (initialFocusRef && initialFocusRef.current) || container;
    if (initialTarget && initialTarget.focus) initialTarget.focus();

    const onKeyDown = (e) => {
      if (modalStack[modalStack.length - 1] !== token) return;

      if (e.key === "Escape") {
        e.preventDefault();
        if (onCloseRef.current) onCloseRef.current();
        return;
      }

      if (e.key === "Tab" && container) {
        const focusables = Array.from(container.querySelectorAll(FOCUSABLE));
        if (focusables.length === 0) {
          e.preventDefault();
          container.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      const index = modalStack.indexOf(token);
      if (index !== -1) modalStack.splice(index, 1);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    };
  }, [open, containerRef, initialFocusRef]);
}
